// Frontend checkout utilities for Stripe.
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PENDING_PLAN_KEY = 'shd_pending_plan';
const PENDING_SEATS_KEY = 'shd_pending_seats';

export function rememberPendingPlan(priceId, seats = 1) {
  try {
    localStorage.setItem(PENDING_PLAN_KEY, priceId);
    localStorage.setItem(PENDING_SEATS_KEY, String(Math.max(1, seats)));
  } catch (e) {
    /* ignore quota errors */
  }
}

export function clearPendingPlan() {
  try {
    localStorage.removeItem(PENDING_PLAN_KEY);
    localStorage.removeItem(PENDING_SEATS_KEY);
  } catch (e) {
    /* ignore */
  }
}

export function getPendingPlan() {
  try {
    const priceId = localStorage.getItem(PENDING_PLAN_KEY);
    const seats = parseInt(localStorage.getItem(PENDING_SEATS_KEY) || '1', 10) || 1;
    return priceId ? { priceId, seats } : null;
  } catch (e) {
    return null;
  }
}

export async function startCheckout(priceId, seats = 1) {
  const { data } = await axios.post(
    `${API}/payments/checkout`,
    {
      price_id: priceId,
      seats: Math.max(1, seats),
      success_path: '/?checkout=success',
      cancel_path: '/?checkout=canceled',
    },
    { withCredentials: true }
  );
  if (!data || !data.url) throw new Error('Checkout session creation failed');
  // Persist session_id so we can poll on return
  try { localStorage.setItem('shd_last_session', data.session_id); } catch (e) { /* ignore */ }
  window.location.href = data.url;
}

export async function pollCheckoutStatus(sessionId, { maxAttempts = 8, intervalMs = 2500 } = {}) {
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      const { data } = await axios.get(`${API}/payments/status/${sessionId}`, { withCredentials: true });
      if (data.payment_status === 'paid') return data;
      if (data.status === 'expired') return data;
    } catch (e) {
      if (attempt === maxAttempts - 1) throw e;
    }
    await new Promise((r) => setTimeout(r, intervalMs));
  }
  return null;
}

export function readCheckoutQueryParams() {
  const params = new URLSearchParams(window.location.search);
  return {
    checkout: params.get('checkout'),
    sessionId: params.get('session_id'),
  };
}

export function clearCheckoutQueryParams() {
  const url = new URL(window.location.href);
  url.searchParams.delete('checkout');
  url.searchParams.delete('session_id');
  window.history.replaceState({}, '', url.toString());
}

export async function fetchPlans() {
  const { data } = await axios.get(`${API}/payments/plans`);
  return data;
}

export function planLabel(priceId, plans) {
  if (!plans || !plans[priceId]) return priceId;
  return plans[priceId].name;
}

export function handleCheckoutError(err) {
  const detail = err?.response?.data?.detail;
  const msg = typeof detail === 'string' ? detail : (err?.message || 'Checkout failed');
  toast.error(msg);
}
